import React, { useMemo, useState } from 'react'

import { toast } from 'react-toastify'

import Heading from 'presentation/shared/components/Heading'
import SupportText from 'presentation/shared/components/SupportText'
import * as S from './styles'
import { Hospital } from 'domain/entities/hospital-model'
import { Doctor } from 'domain/entities/doctor-model'
import { SurgeryCenter } from 'domain/entities/surgery-center-model'
import Chip from 'presentation/shared/components/Chip'
import { useFormikContext } from 'formik'
import { AddSurgicalOrderFormValues } from '../index'
import { useAddSurgicalOrderFunctions } from 'presentation/hospital/pages/AddSurgicalOrder/load-surgical-order'
import Modal from 'presentation/hospital/components/Modal'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import CheckIcon from 'presentation/assets/icons/big-check.svg'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import ButtonNew from 'presentation/shared/components/ButtonNew'

type Props = {
  goNext?: () => void
  hospitals: Hospital[]
  doctors: Doctor[]
  goBack?: () => void
  surgeryCenters: SurgeryCenter[]
  updateOrder?: boolean
} & WithLoadingProps

const SelectDoctorAndHospital = WithLoading(
  ({
    goNext,
    hospitals,
    doctors,
    surgeryCenters,
    updateOrder = false,
    setIsLoading
  }: Props) => {
    const { setFieldValue, values } =
      useFormikContext<AddSurgicalOrderFormValues>()
    const context = useAddSurgicalOrderFunctions()
    const [doctorQuery, setDoctorQuery] = useState<string>()
    const [showModal, setShowModal] = useState<boolean>(false)

    const isValid = useMemo(() => {
      return values.doctor && values.hospital && values.surgery_center
    }, [values.hospital, values.doctor, values.surgery_center])

    async function selectDoctor(doctor: Doctor) {
      if (doctor) {
        await setFieldValue('doctor', doctor)
        const placeholder = `${doctor.crmUf} - ${doctor.crm} - ${doctor.name}`
        setDoctorQuery(placeholder)
      } else {
        await setFieldValue('doctor', undefined)
        setDoctorQuery(undefined)
      }
    }

    const resetAutocompleteDoctor = () => {
      setFieldValue('doctor', undefined)
      setDoctorQuery(undefined)
    }

    async function selectHospital(id: string) {
      try {
        setIsLoading(true)
        const hospital = hospitals.find(
          (hospital) => hospital.hospital_id == Number(id)
        )
        if (hospital) {
          await setFieldValue('hospital', hospital)
          await setFieldValue('surgery_center', undefined)
          await context?.loadSurgeryCenter(hospital.hospital_id ?? 0)
          await context?.loadHealthInsurances(hospital.hospital_id ?? 0)
        } else {
          await setFieldValue('hospital', undefined)
        }
      } catch (err) {
        toast.error('Não foi possível selecionar a unidade')
      } finally {
        setIsLoading(false)
      }
    }

    async function selectSurgeryCenter(surgeryCenter: SurgeryCenter) {
      if (surgeryCenter) {
        await setFieldValue('surgery_center', surgeryCenter)
      } else {
        await setFieldValue('surgery_center', undefined)
      }
    }
    return (
      <S.Wrapper>
        {!updateOrder && (
          <>
            <Heading size="xlarge" color="primary" as="h1">
              Novo pedido cirúrgico
            </Heading>
          </>
        )}

        {!updateOrder ? (
          <SupportText style={{ marginBottom: '3.125em' }}>
            Selecione o médico, a unidade e qual a sala em que o procedimento
            será realizado
          </SupportText>
        ) : (
          <SupportText
            style={{ marginBottom: '1em', fontSize: '18px', fontWeight: 700 }}
          >
            Dados do médico e unidade
          </SupportText>
        )}
        <S.Inputs>
          <div>
            <AutoCompleteNew
              name="doctor"
              placeholder="Busca por nome ou CRM"
              label="Médico"
              value={doctorQuery || values.doctor?.name || ''}
              onType={(val) => context?.loadDoctor?.(val)}
              onInputChange={setDoctorQuery}
              suggestions={doctors.map((doctor) => ({
                label: `${doctor.crmUf} - ${doctor.crm} - ${doctor.name}`,
                value: doctor
              }))}
              onSuggestionClick={selectDoctor}
              clear={() =>
                values.doctor?.doctor_id &&
                !updateOrder &&
                resetAutocompleteDoctor()
              }
              disabled={!!values.doctor?.doctor_id}
              required
              data-testid="select-doctor-autocomplete"
              debounceDelay={800}
              minCharactersSearch={3}
              icon={<SearchIcon width={16} height={16} />}
              iconPosition="insideRight"
              iconColor="neutral400"
            />
          </div>

          <div>
            <SelectFieldNew
              style={{ marginBottom: '1em' }}
              label="Unidade"
              name="hospital"
              items={hospitals?.map(({ name, friendly_name, hospital_id }) => ({
                label: friendly_name || name,
                value: hospital_id
              }))}
              onInputChange={selectHospital}
              data-testid="hospital-select"
              value={values.hospital?.hospital_id ?? ''}
              required
            />

            <S.SurgeryCenterList>
              {surgeryCenters.map((center) => (
                <Chip
                  key={center.codeCenter}
                  dataTestId={center.codeCenter}
                  label={center.description}
                  customValue={center}
                  name="surgery_center"
                  onCheck={() => selectSurgeryCenter(center)}
                  checked={
                    Number(values.surgery_center?.codeCenter) ===
                    center.codeCenter
                  }
                  data-testid={`surgery-center-chip-${values.hospital?.hospital_id}`}
                  disabled={values.hospital?.hospital_id !== center.hospital_id}
                />
              ))}

              {values.hospital?.hospital_id > 0 &&
                surgeryCenters.length === 0 && (
                  <S.NoAvailableSurgeryCenter>
                    Sem centros cirurgícos para a unidade escolhida
                  </S.NoAvailableSurgeryCenter>
                )}
            </S.SurgeryCenterList>
          </div>
        </S.Inputs>
        {!updateOrder && (
          <ButtonNew
            size="large"
            disabled={!isValid}
            type="button"
            onClick={() => setShowModal(true)}
            data-testid={`select-doctor-next-button`}
            style={{
              width: '100%',
              maxWidth: '320px'
            }}
          >
            Próximo
          </ButtonNew>
        )}
        {showModal && (
          <Modal close={() => setShowModal(false)}>
            <S.ModalWrapper>
              <img src={CheckIcon} alt="Check icon" />
              <p>Deseja adicionar pedido na unidade {values.hospital.name}?</p>
              <S.ModalButtons>
                <ButtonNew
                  size="large"
                  type="button"
                  outlined
                  onClick={() => setShowModal(false)}
                  data-testid={`cancel-modal-button`}
                >
                  Cancelar
                </ButtonNew>
                <ButtonNew
                  size="large"
                  type="button"
                  data-testid={`confirm-modal-button`}
                  onClick={goNext}
                >
                  Confirmar
                </ButtonNew>
              </S.ModalButtons>
            </S.ModalWrapper>
          </Modal>
        )}
      </S.Wrapper>
    )
  }
)
export default SelectDoctorAndHospital
